import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, ShieldCheck, Banknote, Info } from "lucide-react";

export default function WalletRetirar() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [cfg, setCfg] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [showNewAcc, setShowNewAcc] = useState(false);
    const [selAcc, setSelAcc] = useState("");
    const [amount, setAmount] = useState(50000);
    const [preview, setPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // new account fields
    const [acc, setAcc] = useState({
        method: "bre_b", holder_name: "", doc_type: "CC", doc_number: "", destination: "", bank: "",
    });

    const loadAccounts = async () => {
        const r = await api.get("/withdrawals/accounts/me");
        setAccounts(r.data || []);
        if (r.data?.length && !selAcc) setSelAcc(r.data[0].id);
        if ((r.data || []).length === 0) setShowNewAcc(true);
    };

    useEffect(() => {
        api.get("/wallet/config/public").then((r) => setCfg(r.data));
        if (user && user !== false) loadAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        if (!amount || amount <= 0) { setPreview(null); return; }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/withdrawals/preview", { amount: Math.round(amount) });
                if (!cancelled) setPreview(data);
            } catch (e) {
                if (!cancelled) setPreview(null);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [amount]);

    if (user === false) return <Navigate to="/login" replace />;
    if (!user || !cfg) return null;

    const registerAccount = async () => {
        if (!acc.holder_name || acc.holder_name.length < 3) { toast.error("Ingresa el nombre del titular"); return; }
        if (!acc.doc_number || acc.doc_number.length < 4) { toast.error("Documento inválido"); return; }
        if (!acc.destination || acc.destination.length < 4) { toast.error("Destino inválido"); return; }
        try {
            await api.post("/withdrawals/accounts", acc);
            toast.success("Cuenta registrada. Queda en verificación.");
            setShowNewAcc(false);
            await loadAccounts();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al registrar");
        }
    };

    const submitWithdraw = async () => {
        if (!selAcc) { toast.error("Selecciona o registra una cuenta de retiro"); return; }
        if (amount < (cfg.min_withdraw || 0)) { toast.error(`Mínimo: ${cfg.min_withdraw}`); return; }
        if (amount > (user.points_balance || 0)) { toast.error("Saldo insuficiente"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/withdrawals", { amount: Math.round(amount), account_id: selAcc });
            if (data.status === "on_hold") {
                toast.message("Retiro en revisión manual: el nombre del titular no coincide con tu perfil.");
            } else {
                toast.success("Retiro solicitado.");
            }
            await refresh();
            navigate("/wallet");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al retirar");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="wallet-retirar-page">
            <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-p2w-gray hover:text-white transition mb-5">
                <ArrowLeft className="w-4 h-4" /> Volver
            </button>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-p2w-blue font-sora font-bold">
                <Banknote className="w-3.5 h-3.5" /> Retirar saldo
            </div>
            <h1 className="font-sora font-extrabold text-4xl sm:text-5xl tracking-tighter mt-2">Retirar saldo</h1>
            <p className="mt-2 text-p2w-gray">Saldo disponible: <strong className="text-white">{(user.points_balance || 0).toLocaleString("es-CO")}</strong> pts</p>

            {/* Accounts */}
            <div className="mt-8 rounded-2xl border border-p2w-line bg-p2w-surface p-6">
                <div className="flex items-center justify-between gap-2">
                    <h2 className="font-sora font-bold text-lg">Cuentas de retiro</h2>
                    <button onClick={() => setShowNewAcc((v) => !v)} data-testid="toggle-new-account" className="text-sm text-p2w-green font-semibold">
                        {showNewAcc ? "Ocultar" : "+ Registrar cuenta"}
                    </button>
                </div>

                {accounts.length > 0 && (
                    <div className="mt-4 space-y-2">
                        {accounts.map((a) => (
                            <label key={a.id} className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border cursor-pointer ${selAcc === a.id ? "border-p2w-green bg-p2w-green/5" : "border-p2w-line bg-p2w-elevated"}`}>
                                <div className="flex items-center gap-3">
                                    <input type="radio" name="acc" checked={selAcc === a.id} onChange={() => setSelAcc(a.id)} data-testid={`account-${a.id}`} className="accent-p2w-green" />
                                    <div>
                                        <div className="text-sm font-semibold text-white">{a.holder_name} <span className="text-p2w-gray font-normal">· {a.doc_type} {a.doc_number}</span></div>
                                        <div className="text-xs text-p2w-gray font-mono">{a.method === "bre_b" ? "Bre-B" : "USDT TRC20"} · {a.destination}</div>
                                    </div>
                                </div>
                                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md border ${a.status === "verified" ? "border-p2w-green/40 bg-p2w-green/15 text-p2w-green" : "border-p2w-amber/40 bg-p2w-amber/15 text-p2w-amber"}`}>
                                    {a.status === "verified" ? "Verificada" : "Pendiente"}
                                </span>
                            </label>
                        ))}
                    </div>
                )}

                {showNewAcc && (
                    <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="new-account-form">
                        <Field label="Método">
                            <Select value={acc.method} onValueChange={(v) => setAcc({ ...acc, method: v })}>
                                <SelectTrigger data-testid="acc-method" className="bg-p2w-elevated border-p2w-line text-white"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-p2w-elevated border-p2w-line text-white">
                                    <SelectItem value="bre_b">Bre-B</SelectItem>
                                    <SelectItem value="crypto">Crypto USDT TRC20</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Banco / Wallet (opcional)">
                            <Input value={acc.bank} onChange={(e) => setAcc({ ...acc, bank: e.target.value })} data-testid="acc-bank" className="bg-p2w-elevated border-p2w-line text-white" />
                        </Field>
                        <Field label="Titular (nombre y apellido)" required>
                            <Input value={acc.holder_name} onChange={(e) => setAcc({ ...acc, holder_name: e.target.value })} data-testid="acc-holder" className="bg-p2w-elevated border-p2w-line text-white" />
                        </Field>
                        <Field label="Tipo de documento">
                            <Select value={acc.doc_type} onValueChange={(v) => setAcc({ ...acc, doc_type: v })}>
                                <SelectTrigger data-testid="acc-doctype" className="bg-p2w-elevated border-p2w-line text-white"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-p2w-elevated border-p2w-line text-white">
                                    <SelectItem value="CC">CC</SelectItem>
                                    <SelectItem value="CE">CE</SelectItem>
                                    <SelectItem value="NIT">NIT</SelectItem>
                                    <SelectItem value="PAS">Pasaporte</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Número de documento" required>
                            <Input value={acc.doc_number} onChange={(e) => setAcc({ ...acc, doc_number: e.target.value })} data-testid="acc-docnum" className="bg-p2w-elevated border-p2w-line text-white" />
                        </Field>
                        <Field label={acc.method === "bre_b" ? "Llave Bre-B / Cuenta" : "Dirección USDT TRC20"} required>
                            <Input value={acc.destination} onChange={(e) => setAcc({ ...acc, destination: e.target.value })} data-testid="acc-destination" className="bg-p2w-elevated border-p2w-line text-white" />
                        </Field>
                        <div className="sm:col-span-2">
                            <div className="flex items-start gap-2 rounded-lg border border-p2w-blue/40 bg-p2w-blue/10 px-3 py-2 mb-3">
                                <ShieldCheck className="w-4 h-4 text-p2w-blue mt-0.5 shrink-0" />
                                <p className="text-xs text-p2w-blue">El nombre del titular debe coincidir con tu perfil. Si no coincide, el retiro queda en revisión manual.</p>
                            </div>
                            <Button onClick={registerAccount} data-testid="submit-new-account" className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold">
                                Registrar cuenta
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Amount + breakdown */}
            <div className="mt-6 rounded-2xl border border-p2w-line bg-p2w-surface p-6 space-y-5">
                <Field label="Monto a retirar (pts)" required>
                    <Input data-testid="withdraw-amount" type="number" min={cfg.min_withdraw} step={1000} value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="bg-p2w-elevated border-p2w-line text-white" />
                    <div className="mt-1 text-xs text-p2w-gray">Mínimo: {Number(cfg.min_withdraw || 0).toLocaleString("es-CO")} · Máximo diario sin retención: {Number(cfg.withdraw_daily_max || 0).toLocaleString("es-CO")}</div>
                </Field>

                {preview && (
                    <div data-testid="withdraw-breakdown" className="rounded-xl border border-p2w-line bg-p2w-elevated p-4 space-y-2">
                        <Row label="Monto solicitado" value={preview.amount} />
                        <Row label="Retirado últimas 24h" value={preview.used_24h} />
                        <Row label="Máximo diario" value={preview.daily_max} />
                        {preview.over_limit ? (
                            <>
                                <div className="border-t border-p2w-line pt-2">
                                    <div className="flex items-start gap-2 rounded-lg border border-p2w-amber/40 bg-p2w-amber/10 px-3 py-2 mb-2">
                                        <Info className="w-4 h-4 text-p2w-amber mt-0.5 shrink-0" />
                                        <p className="text-xs text-p2w-amber">
                                            {preview.withholding_label || "Retención aplicada"} ({Math.round(preview.withholding_pct * 100)}% sobre {preview.withholding_applies === "total" ? "el total" : "el excedente"}).
                                        </p>
                                    </div>
                                    <Row label="Retención" value={-preview.withholding_amount} color="text-p2w-red" />
                                    <Row label="Neto a recibir" value={preview.net_amount} color="text-p2w-green" bold />
                                </div>
                                <Link to="/legal/terminos" className="text-xs text-p2w-blue underline">Ver política de retenciones</Link>
                            </>
                        ) : (
                            <Row label="Neto a recibir" value={preview.net_amount} color="text-p2w-green" bold />
                        )}
                    </div>
                )}

                <Button onClick={submitWithdraw} disabled={submitting || !selAcc} data-testid="submit-withdraw" className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold py-6 rounded-xl">
                    {submitting ? "Enviando…" : "Confirmar retiro"}
                </Button>
                {accounts.length > 0 && !accounts.find((a) => a.id === selAcc && a.status === "verified") && (
                    <div className="flex items-start gap-2 rounded-lg border border-p2w-amber/40 bg-p2w-amber/10 px-3 py-2">
                        <AlertTriangle className="w-4 h-4 text-p2w-amber mt-0.5 shrink-0" />
                        <p className="text-xs text-p2w-amber">Esta cuenta aún no está verificada. El retiro quedará en revisión.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children, required }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-widest text-p2w-gray">{label} {required && <span className="text-p2w-amber">*</span>}</Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function Row({ label, value, color, bold }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm text-p2w-gray">{label}</span>
            <span className={`text-sm number-tabular ${bold ? "font-sora font-bold" : ""} ${color || "text-white"}`}>
                {Number(value || 0).toLocaleString("es-CO")}
            </span>
        </div>
    );
}
